import _, {isUndefined} from 'lodash';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {compareName} from '../../../../srcCommon/helpers/localeHelpers';
import {CardModel, Link} from '../../../models/api/cardModel';
import {ContactSourcesEnum, parseContactUid} from '../../../models/api/contactModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {ContactCardLocationsEnum} from '../../../store/panels/contacts/contactCard/contactCardPanelState';
import {ContactDetailsScrollSectionsEnum} from '../../../store/panels/contacts/contactDetailsPanelState';
import {ContactCardPanelId} from '../../../store/panels/panelStateBase';
import {Skeleton, SkeletonColorSchemesEnum} from '../../common/skeleton';
import {computeContactCardLocationTracking} from '../common/contactCardTrackingHelpers';
import {renderContactCardHandle, RenderHandleOptions} from '../helpers/contactCardHandleHelpers';
import {ContactCardDetailsCustomFieldsContainer} from './contactCardDetailsCustomFieldsContainer';
import {ContactCardViewMoreButton} from './contactCardViewMoreButton';
import {ContactCardActionItemContainer} from './items/contactCardActionItemContainer';
import {ContactCardChildItem} from './items/contactCardChildItem';
import {ContactCardLinkItem} from './items/contactCardLinkItem';

/*
 * Constants.
 */

const maxCardItems = 4;
const maxNotesCount = 20;
const maxConversationsCount = 20;
const contactSourcesToDisplay = [
  ContactSourcesEnum.EMAIL,
  ContactSourcesEnum.PHONE,
  ContactSourcesEnum.TWITTER,
  ContactSourcesEnum.TWITTER_DM,
  ContactSourcesEnum.CUSTOM,
  ContactSourcesEnum.FACEBOOK,
];

/*
 * Props.
 */

interface ContactCardCardDetailsProps {
  panelId: ContactCardPanelId;
  card: CardModel;
  location: ContactCardLocationsEnum;
  conversationCount?: number;
  conversationStatus?: AsyncStatusesEnum;
}

/*
 * Styles.
 */

const StyledActionsWrapperDiv = styled.div`
  margin-top: -6px;
  grid-column: span 2;

  &:empty {
    display: none;
  }
`;

const StyledLoadingSkeleton = styled(Skeleton)`
  margin: 8px 0 5px 0;

  &:last-child {
    margin-bottom: -3px;
  }
`;

/*
 * Component.
 */

export const ContactCardCardDetails: FC<ContactCardCardDetailsProps> = (props) => {
  const {panelId, card, location} = props;
  const [isShowingAdditionalFields, setIsShowingAdditionalFields] = useState(false);

  const maxFieldsToDisplay = computeMaxItemsToRender(isShowingAdditionalFields);

  const renderedHandles = useMemo(
    () => renderHandles(card.contactUids, maxFieldsToDisplay),
    [card.contactUids, maxFieldsToDisplay],
  );

  return (
    <>
      {renderedHandles}
      {maybeRenderDescription(card, maxFieldsToDisplay)}
      {renderLinks(card.links, computeAvailableLinksToRender(card, maxFieldsToDisplay))}
      <ContactCardDetailsCustomFieldsContainer
        customAttributes={card.customFieldAttributes ?? []}
        // Calculate the room based on the already rendered fields.
        availableItemsToRender={computeAvailableCustomFieldsToRender(card, maxFieldsToDisplay)}
      />
      {maybeRenderViewMoreFields(card, isShowingAdditionalFields, setIsShowingAdditionalFields)}
      <StyledActionsWrapperDiv>
        {maybeRenderConversations(props)}
        {renderCardNotes(panelId, card, location)}
      </StyledActionsWrapperDiv>
    </>
  );
};

/*
 * Helpers.
 */

function computeValidHandles(contactUids: ReadonlyArray<string>) {
  const parsedContactUids = contactUids.map(parseContactUid);
  return _(contactSourcesToDisplay)
    .map((contactSource) => filterUidsForContactSource(parsedContactUids, contactSource))
    .flatten()
    .value();
}

function computeMaxItemsToRender(isShowingAdditionalFields: boolean) {
  return isShowingAdditionalFields ? Number.MAX_SAFE_INTEGER : maxCardItems;
}

function renderHandles(contactUids: ReadonlyArray<string>, maxFieldsToDisplay: number) {
  const handles = _(computeValidHandles(contactUids)).slice(0, maxFieldsToDisplay).groupBy('source').value();

  return (
    <>
      {_(handles)
        .keys()
        .map((sourceKey) => handles[sourceKey].map(renderContactCardHandle))
        .value()}
    </>
  );
}

function filterUidsForContactSource(
  parsedContactUids: ReadonlyArray<ReadonlyArray<string>>,
  source: ContactSourcesEnum,
): ReadonlyArray<RenderHandleOptions> {
  return parsedContactUids
    .filter((contactUid) => contactUid[0] === source)
    .sort((a, b) => compareName(a[1], b[1]))
    .map((contactUid) => ({handle: contactUid[1], source}));
}

function maybeRenderDescription(card: CardModel, maxFieldsToDisplay: number) {
  if (!card.description || !computeShouldRenderDescription(card, maxFieldsToDisplay)) {
    return null;
  }
  return (
    <ContactCardChildItem
      text={card.description}
      title={<Trans id="contact-card-details-description-title">Description</Trans>}
      shouldAllowMultiLineWrapping
    >
      {card.description}
    </ContactCardChildItem>
  );
}

function renderLinks(links: ReadonlyArray<Link>, availableItems: number) {
  const linksToRender = links.slice(0, availableItems);
  if (linksToRender.length === 0) {
    return null;
  }
  return (
    <>
      {linksToRender.map((link, index) => (
        <ContactCardLinkItem
          key={link.url}
          link={link}
          title={index === 0 ? <Trans id="contact-card-details-link-title">Link</Trans> : undefined}
        />
      ))}
    </>
  );
}

function computeShouldRenderDescription(card: CardModel, maxFieldsToDisplay: number) {
  if (card.contactUids.length >= maxFieldsToDisplay) {
    return false;
  }
  return true;
}

function computeAvailableLinksToRender(card: CardModel, maxFieldsToDisplay: number) {
  return Math.max(0, maxFieldsToDisplay - card.contactUids.length - (card.description ? 1 : 0));
}

function computeAvailableCustomFieldsToRender(card: CardModel, maxFieldsToDisplay: number) {
  return Math.max(0, computeAvailableLinksToRender(card, maxFieldsToDisplay) - card.links.length);
}

function maybeRenderViewMoreFields(
  card: CardModel,
  isShowingAdditionalFields: boolean,
  setIsShowingAdditionalFields: HandlerOf<boolean>,
) {
  if (isShowingAdditionalFields) {
    return null;
  }

  const handles = computeValidHandles(card.contactUids);
  // Add all the fields together.
  const totalAvailableItems =
    handles.length +
    card.links.length +
    (card.customFieldAttributes ?? []).length +
    (card.description ? 1 : 0);
  const additionalItemsCount = Math.max(0, totalAvailableItems - maxCardItems);

  if (!additionalItemsCount) {
    return null;
  }
  return (
    <ContactCardViewMoreButton
      additionalItemsCount={additionalItemsCount}
      onClick={() => setIsShowingAdditionalFields(true)}
    />
  );
}

function renderCardNotes(panelId: ContactCardPanelId, card: CardModel, location: ContactCardLocationsEnum) {
  const {numberOfNotesViaNamespaces: notesCount} = card;
  const isAtMaxNotes = notesCount >= maxNotesCount;
  const notesCountToRender = isAtMaxNotes ? maxNotesCount : notesCount;

  // If we have no notes on the card, render nothing.
  if (notesCountToRender === 0) {
    return null;
  }
  return (
    <ContactCardActionItemContainer
      panelId={panelId}
      iconName="noteSmall"
      card={card}
      scrollToSection={ContactDetailsScrollSectionsEnum.NOTES}
      onTrackEvent={() => {
        trackInterfaceEvent(InterfaceEventsEnum.CONTACT_CARD_CONVERSATIONS_CLICK, {
          has_associated_account: Boolean(card.accountId),
          namespace: card.namespace,
          location: computeContactCardLocationTracking(location),
        });
      }}
    >
      {notesCountToRender}
      {isAtMaxNotes && <Trans id="contact-card-details-notes-additional-indicator">+</Trans>}
      &nbsp;
      <Plural id="contact-card-details-notes-count" value={notesCountToRender} one="note" other="notes" />
    </ContactCardActionItemContainer>
  );
}

function maybeRenderConversations(props: ContactCardCardDetailsProps) {
  const {panelId, card, conversationCount, conversationStatus, location} = props;

  // If we are loading, display a skeleton.
  if (conversationStatus === AsyncStatusesEnum.LOADING) {
    return <StyledLoadingSkeleton width="238px" height="16px" scheme={SkeletonColorSchemesEnum.GREY10} />;
  }

  // If we do not have any values loaded or no conversations, display nothing.
  if (
    isUndefined(conversationCount) ||
    conversationStatus !== AsyncStatusesEnum.LOADED ||
    conversationCount === 0
  ) {
    return null;
  }

  const isAtMaxConversations = conversationCount >= maxConversationsCount;
  const conversationsCountToRender = isAtMaxConversations ? maxConversationsCount : conversationCount;

  return (
    <ContactCardActionItemContainer
      panelId={panelId}
      iconName="openSmall"
      card={card}
      scrollToSection={ContactDetailsScrollSectionsEnum.CONVERSATIONS}
      onTrackEvent={() => {
        trackInterfaceEvent(InterfaceEventsEnum.CONTACT_CARD_CONVERSATIONS_CLICK, {
          has_associated_account: Boolean(card.accountId),
          namespace: card.namespace,
          location: computeContactCardLocationTracking(location),
        });
      }}
    >
      {conversationsCountToRender}
      {isAtMaxConversations && <Trans id="contact-card-details-conversations-additional-indicator">+</Trans>}
      &nbsp;
      <Plural
        id="contact-card-details-conversations-count"
        value={conversationsCountToRender}
        one="conversation in the last 7 days"
        other="conversations in the last 7 days"
      />
    </ContactCardActionItemContainer>
  );
}
